/**
 * ActivityBar — Vertical icon rail with category icons (VS Code style)
 */
import React from 'react';
import { Box, IconButton, Tooltip, Divider } from '@mui/material';
import {
  Layers,
  Palette,
  Calculate,
  Edit,
  Brush,
  Straighten,
  Bookmark,
  Search,
  Print,
  Public,
  // Professional role icons
  GpsFixed as SurveyIcon, // Survey / COGO / Station Target
  Architecture, // Design / Civil
  Terrain, // Terrain & 3D
  PhoneAndroid, // Field / Mobile
} from '@mui/icons-material';
import { tokens } from '../../theme';
import { useGeoveraTheme } from '../../theme/ThemeContext';
import type { ActivityId } from './AppShell';

interface ActivityItem {
  id: NonNullable<ActivityId>;
  label: string;
  icon: React.ReactNode;
  shortcut?: string;
}

const ACTIVITIES: ActivityItem[] = [
  { id: 'layers', label: 'My Layers', icon: <Layers sx={{ fontSize: 22 }} />, shortcut: '⌘1' },
  {
    id: 'symbology',
    label: 'How it looks',
    icon: <Palette sx={{ fontSize: 22 }} />,
    shortcut: '⌘2',
  },
  { id: 'analysis', label: 'Analysis', icon: <Calculate sx={{ fontSize: 22 }} />, shortcut: '⌘3' },
  { id: 'edit', label: 'Edit', icon: <Edit sx={{ fontSize: 22 }} />, shortcut: '⌘4' },
  { id: 'drawing', label: 'Draw on map', icon: <Brush sx={{ fontSize: 22 }} />, shortcut: '⌘5' },
  { id: 'measure', label: 'Measure', icon: <Straighten sx={{ fontSize: 22 }} />, shortcut: '⌘6' },
  // === Professional Role Activities (Future GeoVera) — friendly names for non-tech ===
  {
    id: 'survey',
    label: 'Measure & Survey',
    icon: <SurveyIcon sx={{ fontSize: 22 }} />,
    shortcut: '⌘7',
  },
  {
    id: 'design',
    label: 'Plan & Design',
    icon: <Architecture sx={{ fontSize: 22 }} />,
    shortcut: '⌘8',
  },
  { id: 'terrain', label: '3D & Terrain', icon: <Terrain sx={{ fontSize: 22 }} />, shortcut: '⌘9' },
  {
    id: 'field',
    label: 'Field Work',
    icon: <PhoneAndroid sx={{ fontSize: 22 }} />,
    shortcut: '⌘0',
  },
];

const SECONDARY_ACTIVITIES: ActivityItem[] = [
  { id: 'bookmarks', label: 'Saved Places', icon: <Bookmark sx={{ fontSize: 22 }} /> },
  { id: 'search', label: 'Find on map', icon: <Search sx={{ fontSize: 22 }} /> },
  { id: 'crs', label: 'Map Coordinates', icon: <Public sx={{ fontSize: 22 }} /> },
  { id: 'print', label: 'Print & Export', icon: <Print sx={{ fontSize: 22 }} /> },
];

interface ActivityBarProps {
  activeActivity: ActivityId;
  onActivityChange: (id: ActivityId) => void;
}

const ActivityBar: React.FC<ActivityBarProps> = ({ activeActivity, onActivityChange }) => {
  const { palette } = useGeoveraTheme();

  const renderActivity = (item: ActivityItem) => {
    const isActive = activeActivity === item.id;
    return (
      <Tooltip
        key={item.id}
        title={
          <Box>
            <Box sx={{ fontWeight: 600 }}>{item.label}</Box>
            {item.shortcut && (
              <Box sx={{ fontSize: tokens.fontSize.xs, opacity: 0.7, mt: 0.25 }}>
                {item.shortcut}
              </Box>
            )}
          </Box>
        }
        placement="right"
        arrow
      >
        <Box sx={{ position: 'relative' }}>
          {/* Active indicator */}
          {isActive && (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 8,
                bottom: 8,
                width: 2,
                backgroundColor: palette.accent.primary,
                borderRadius: '0 2px 2px 0',
              }}
            />
          )}
          <IconButton
            onClick={() => onActivityChange(item.id)}
            sx={{
              width: tokens.layout.activityBarWidth,
              height: tokens.layout.activityBarWidth,
              borderRadius: 0,
              color: isActive ? palette.text.primary : palette.text.muted,
              transition: `color ${tokens.animation.fast}`,
              '&:hover': {
                color: palette.text.primary,
                backgroundColor: 'transparent',
              },
            }}
            aria-label={item.label}
            aria-pressed={isActive}
          >
            {item.icon}
          </IconButton>
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        width: tokens.layout.activityBarWidth,
        minWidth: tokens.layout.activityBarWidth,
        flexShrink: 0,
        height: '100%',
        backgroundColor: palette.bg.raised,
        borderRight: `1px solid ${palette.border.base}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 0.5,
        zIndex: 5,
      }}
    >
      {/* Primary activities */}
      {ACTIVITIES.map(renderActivity)}

      <Divider
        sx={{
          width: '60%',
          my: 1,
          borderColor: palette.border.base,
        }}
      />

      {/* Secondary activities */}
      {SECONDARY_ACTIVITIES.map(renderActivity)}
    </Box>
  );
};

export default ActivityBar;
